import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { SerialBond, TermBond } from "../debt-service-page";
import { DealBuilder } from "../../services/deal-builder";

export type DragSource = (
  { bond: SerialBond } |
  ({ bond: TermBond } & { subType: "principal", index: number }) |
  ({ bond: TermBond } & { subType: "redemption", index: number })
)

export type DragTarget = DragSource & { position: "top" | "bottom" | "middle" };

interface DragAndDropContextProps {
  source: DragSource | undefined,
  setSource: React.Dispatch<React.SetStateAction<DragSource | undefined>>,
  target: DragTarget | undefined,
  setTarget: React.Dispatch<React.SetStateAction<DragTarget | undefined>>,
  isCurrentSource: (source: DragSource) => boolean,
  getTargetStatus: (builder: DealBuilder, source: DragSource) => "none" | "top" | "bottom" | "middle",
  subscribe: (cb: (source: DragSource, target: DragTarget) => void) => () => void
}

const DragAndDropContext = createContext<DragAndDropContextProps>(null as any);

export const DragAndDropProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [source, setSource] = useState<DragSource>();
  const [target, setTarget] = useState<DragTarget>();
  
  const [subscribers, setSubscribers] = useState<((source: DragSource, target: DragTarget) => void)[]>([]);

  useEffect(() => {
    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      if (source && target && source.bond !== target.bond) {
        onDragAndDrop(source, target);
      }

      setSource(undefined);
      setTarget(undefined);
    };

    document.addEventListener("drop", handleDrop);
    return () => document.removeEventListener("drop", handleDrop);
  }, [source, target]);

  function onDragAndDrop(source: DragSource, target: DragTarget) {
    for (const cb of subscribers) {
      cb(source, target);
    }
  }

  function isCurrentSource(input: DragSource) {
    if (!source) return false;
    if (source.bond.type !== input.bond.type) return false;
    if (source.bond.type === "serial" || input.bond.type === "serial" ) return input.bond === source.bond;
    if ("subType" in source && "subType" in input) return source.subType === input.subType && source.index === input.index && input.bond === source.bond;
    return false;
  }

  function isCurrentTarget(input: DragSource) {
    if (!target) return false;
    if (target.bond.type !== input.bond.type) return false;
    if (target.bond.type === "serial" || input.bond.type === "serial" ) return input.bond === target.bond;
    if ("subType" in target && "subType" in input) return target.subType === input.subType && target.index === input.index && input.bond === target.bond;
    return false;
  }

  function getTargetStatus(builder: DealBuilder, input: DragSource) {
    if (!source || !target) return "none";

    const sourceIndex = builder.bonds.indexOf(source.bond);
    const targetIndex = builder.bonds.indexOf(target.bond);
    const inputIndex = builder.bonds.indexOf(input.bond);
    if ([sourceIndex, targetIndex, inputIndex].includes(-1)) return "none";

    const smaller = sourceIndex < targetIndex ? sourceIndex : targetIndex;
    const bigger = sourceIndex > targetIndex ? sourceIndex : targetIndex;
    if (inputIndex >= smaller && inputIndex <= bigger) return target.position;

    return "none";
  }

  function subscribe(cb: (source: DragSource, target: DragTarget) => void) {
    setSubscribers((prev) => [...prev, cb]);
    return () => setSubscribers(curr => curr.filter(x => x !== cb));
  }

  return <DragAndDropContext.Provider value={{
    source, setSource,
    target, setTarget,
    isCurrentSource,
    getTargetStatus,
    subscribe,
  }}>{children}</DragAndDropContext.Provider>;
};

export const useDragAndDropContext = (): DragAndDropContextProps => {
  const context = useContext(DragAndDropContext);
  if (!context) throw new Error("useDragAndDropTheme must be used within a DragAndDropProvider");
  return context;
};
